import { scaleLinear } from "d3-scale";
import { unique } from "../../../utils/func";
import tableTotal, { clearIfSameKeys, getSubTotalRow } from "../tableTotal";
import { hexToRgba } from "../../../styles/colorConvertor";
import { groupBy } from "lodash-es";
import { setFiscalQuearterOffset } from "../../../utils/formatters/formatter";
import { getMappingType } from "../../../utils/getVisualizationLabel";
import { rightAligns } from "../../../utils/constants/constants";
import { isDate } from "../../../utils/formatters/dateFormatter";
import {
  startOfWeek,
  endOfWeek,
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
} from "date-fns";
import {
  dateFromMonth,
  dateFromQuarter,
  dateFromWeek,
} from "../../../utils/dates/dateFunc";

export function getPinnedRows(dataRow, pinnedRows = []) {
  return (pinnedRows ?? []).find(
    (pin) =>
      dataRow[pin.column] &&
      (dataRow[pin.column] === pin.value || dataRow[pin.column] === pin.label)
  );
}

export default function tableMapper(
  aTableCollection,
  rowMainKey,
  groupingKey,
  uniqueValueKey,
  valueKeyAsSubTitle, // suppresses header column duplicate render
  totals,
  totalsAverages,
  pinnedRows,
  groupingDataConfig,
  subTotalRow,
  totalsPositionTop,
  totalsCharts,
  groupingExtraColumns,
  rawRowsValueKey,
  totalColumnsStatic,
  excludeRowFromTotalCalculation,
  totalsOverride,
  progressBarsSettings,
  noCalcKeys,
  rowGroupKeys,
  totalLabel,
  distinctTotalsCounts
) {
  const data = aTableCollection.data;

  const result = {
    headers: aTableCollection.headers,
    rows: [],
    totals: null,
    totalsForExtraColumns: [],
  };

  function getUnPinnedRows(dataRow) {
    return !pinnedRows.find(
      (pin) => dataRow[pin.column] && dataRow[pin.column] === pin.value
    );
  }

  const pinned = pinnedRows
    ? data.filter((dataRow) => getPinnedRows(dataRow, pinnedRows))
    : null;

  const useRawValues = !rowMainKey && !uniqueValueKey;

  let groupedData = data;

  if (groupingDataConfig && data.length > 0) {
    const { key, groups, includeOthers, exclude } = groupingDataConfig;
    const includeConditions = groups.map((group) => group.include).flat();
    const others = includeOthers
      ? data.filter(
          (d) =>
            !includeConditions.filter((ic) => ic !== exclude).includes(d[key])
        )
      : [];

    groupedData = others;
    groups.forEach((group) => {
      const { position, label, include } = group;
      const range = data.filter((d) => include.includes(d[key]));
      const grouped = tableTotal(
        range,
        rowMainKey,
        totalsAverages,
        label,
        null,
        totalColumnsStatic,
        undefined,
        totalsOverride,
        noCalcKeys,
        distinctTotalsCounts
      );

      if (range.length === 0) {
        return;
      }

      if (position === "top") {
        groupedData.unshift(grouped);
      } else {
        groupedData.push(grouped);
      }
    });
  }

  if (subTotalRow) {
    const { groupingKey, getKey, setKey } = subTotalRow;
    const groups = groupBy(groupedData, groupingKey);

    groupedData = Object.values(groups)
      .map((group) => getSubTotalRow(group, getKey, setKey, groupingKey))
      .map((group) => clearIfSameKeys(group, getKey, setKey, groupingKey))
      .flat();
  }

  const mainColumnValues = unique(
    groupedData
      .filter((d) => (pinnedRows ? getUnPinnedRows(d) : true))
      .map((d) => d[uniqueValueKey || rowMainKey])
      .filter(Boolean)
  );

  function setRowKeyValue(row, item) {
    if (valueKeyAsSubTitle) {
      return {};
    }

    return {
      [rowMainKey]: row[0][rowMainKey],
      ...(rowGroupKeys || []).reduce((acc, curr) => {
        acc[curr] = row[0][curr];
        return acc;
      }, {}),
    };
  }

  // this need to add extra column and use totals for calculation
  if (totals) {
    result.totalsForExtraColumns = groupingKey
      ? setGroupingTotals()
      : setRegularTotals();
  }

  mainColumnValues.filter(Boolean).forEach((item, i) => {
    const row = groupedData.filter(
      (d) => d[uniqueValueKey || rowMainKey] === item
    );

    const rowKeyValue = setRowKeyValue(row, item);

    result.rows[i] = {
      ...rowKeyValue,
      values: row,
    };

    if (groupingKey) {
      setGroupingKeyStyleValues(
        groupedData,
        result,
        groupingKey,
        i,
        groupingExtraColumns,
        result.totalsForExtraColumns
      );
    }
  });

  if (useRawValues) {
    result.rows = groupedData
      .filter((d) => (pinnedRows ? getUnPinnedRows(d) : true))
      .map((d) => ({ values: [d] }));
  }

  if (pinnedRows && pinned.length) {
    const keepOrdered =
      pinnedRows[0].position === "top"
        ? pinnedRows.slice().reverse()
        : pinnedRows;

    keepOrdered.forEach((pinnedRow) => {
      const { position, label, column, value } = pinnedRow;
      if (label) {
        const index = pinned.findIndex((pin) => pin[column] === value);
        pinned[index][column] = label;
      }

      function sortPinned() {
        const uniqueGroupingKeys = unique(data.map((d) => d[groupingKey]));
        return uniqueGroupingKeys.reduce((acc, curr) => {
          const pin = pinned.find((p) => p[groupingKey] === curr);
          acc.push(pin);
          return acc;
        }, []);
      }

      // we need to keep order of pinned row because when we cut it from original data we loose the order
      // order is based on headers (uniqueGroupingKeys) see above
      const orderedPinned = groupingKey
        ? sortPinned()
        : pinned.filter(
            (pin) =>
              pin[pinnedRow.column] === pinnedRow.value ||
              pin[pinnedRow.column] === pinnedRow.label
          );

      if (!orderedPinned.length) {
        return;
      }

      if (position === "bottom") {
        result.rows.push({
          values: orderedPinned,
        });
      } else {
        result.rows.unshift({
          values: orderedPinned,
        });
      }
    });
  }

  // calculate totals for regular tables
  function setRegularTotals() {
    const withSkipOnTotals = groupingDataConfig?.skipOnTotals
      ? data
      : groupedData;

    const totalRow = getTableTotal(withSkipOnTotals);

    return [totalRow];
  }

  // calculate totals for static/dynamic groupings
  function setGroupingTotals() {
    if (!groupedData.length) {
      return [];
    }

    const uniqueKeys = [
      ...new Set(groupedData.map((item) => item[groupingKey])),
    ];

    return uniqueKeys.map((key) => {
      const sectionData = groupedData.filter(
        (item) => item[groupingKey] === key
      );

      const total = getTableTotal(sectionData);

      return { ...total, [groupingKey]: key };
    });
  }

  if (totals) {
    const totals = groupingKey ? setGroupingTotals() : setRegularTotals();

    const total = {
      values: totals,
    };

    // this is where totals come from
    if (totalsPositionTop) {
      result.rows.unshift(total);
    } else {
      result.rows.push(total);
    }

    // for some tables we need to use total value to get correct percentage of progress bars
    const { calculateFromTotals } = progressBarsSettings ?? {};
    if (calculateFromTotals) {
      result.totals = totals[0];
    }
  }

  aTableCollection.setRows(result.rows);
  aTableCollection.setProgressBarsTotals(result.totals);

  function getTableTotal(data) {
    return tableTotal(
      data,
      uniqueValueKey || rowMainKey || rawRowsValueKey,
      totalsAverages,
      totalLabel,
      totalsCharts,
      totalColumnsStatic,
      excludeRowFromTotalCalculation,
      totalsOverride,
      noCalcKeys,
      distinctTotalsCounts
    );
  }
}

function setGroupingKeyStyleValues(
  data,
  result,
  groupingKey,
  i,
  groupingExtraColumns,
  totals
) {
  const uniqueGroupingKeys = unique(data.map((d) => d[groupingKey]));
  const tempValues = [...result.rows[i].values];

  uniqueGroupingKeys.forEach((key, j) => {
    const exists = valueExists();
    const notExists = valueNotExists();
    result.rows[i].values[j] = exists || { ...notExists, [groupingKey]: key };

    if (groupingExtraColumns) {
      caclExtraColumns(
        result.rows[i].values,
        groupingExtraColumns,
        j,
        totals[j]
      );
    }

    function valueExists() {
      return tempValues.find((t) => t[groupingKey] === key);
    }

    function valueNotExists() {
      const dataKeys = Object.keys(data[0]);
      return dataKeys.reduce((acc, curr) => {
        return { ...acc, [curr]: null };
      }, {});
    }
  });
}

export function getCellBackground(keys, currentKey, value, theme) {
  if (
    !keys.includes(currentKey) ||
    ["--", null, undefined, NaN].includes(value)
  ) {
    return null;
  }

  if (isNaN(+value) && ["yes", "no"].includes(value.toLowerCase().trim())) {
    const type =
      value.toLowerCase().trim() === "no" ? "errorMain" : "successMain";

    return theme.notification[type];
  }

  const color = theme.notification[value < 0 ? "errorMain" : "successMain"];
  const linear = scaleLinear().domain([0, 10]).range([0, 1]);

  return hexToRgba(color, linear(Math.min(10, value)));
}

function caclExtraColumns(values, config, j, total) {
  const { column, rowKeys, operator } = config;

  const [key1, key2] = rowKeys;

  // we have cases where we need start from 0 index
  if (operator === "DIFFERENCE_TO_TOTALS" && total) {
    values[j][column] = values[j][key1] / total[key1];
  }

  // for cases which using previous values j should be > 0
  if (j === 0) {
    return;
  }

  switch (operator) {
    case "PERCENT_DIFFERENCE": {
      const prev = values[j - 1][key1] / values[j - 1][key2];
      const current = values[j][key1] / values[j][key2];
      values[j][column] = (current - prev) / prev;
      break;
    }

    case "PERCENT_DIFFERENCE_ONE_KEY": {
      const prev = values[j - 1][key1] / values[j - 1][key2];
      const current = values[j][key1] / values[j][key2];
      values[j][column] = (current - prev) / prev;
      break;
    }

    case "DIFFERENCE": {
      const prev = values[j - 1][key1];
      const current = values[j][key1];
      values[j][column] = current - prev;
      break;
    }

    default:
      break;
  }
}

/**
 * Returns formatted start–end range string for a given aggregation code
 * e.g. "2025 W28", "2025 M5", "2025 Q2"
 */
export function getAggregatedDateRange(str) {
  if (!str) return null;

  const dateFormat = "MM/dd/yyyy";
  const match = str.match(/(\d{4})\s*([WMQ])\s*(\d{1,2})/);

  if (!match) return null;

  const [, yearStr, type, numStr] = match;
  const year = Number(yearStr);
  const num = Number(numStr);

  // helper: build date based on aggregation type
  const getBaseDate = () => {
    switch (type) {
      case "W":
        return dateFromWeek(num, year);
      case "M":
        return dateFromMonth(num, year);
      case "Q":
        return dateFromQuarter(num, year);
      default:
        return null;
    }
  };

  const date = getBaseDate();
  if (!date) return null;

  // map each type to its start/end date functions
  const rangeMap = {
    W: [startOfWeek, endOfWeek],
    M: [startOfMonth, endOfMonth],
    Q: [startOfQuarter, endOfQuarter],
  };

  const [getStart, getEnd] = rangeMap[type];
  const start = getStart(date);
  const end = getEnd(date);

  return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
}

export function setParentHeadersMapping(
  item,
  mapping,
  key,
  fiscalQuarterStartOffset
) {
  if (mapping === "[WEEK_RANGE_TO_US_DATE]") {
    const options = { month: "2-digit", day: "2-digit", year: "numeric" };
    const splited = (item[key] + "").split(" - ");

    if (splited.length === 1) {
      const range = getAggregatedDateRange(splited[0]);

      if (range) {
        return { ...item, [key]: range };
      }
    }

    if (!isDate(splited[0]) || !isDate(splited[1])) {
      return {
        ...item,
        [key]: item[key],
      };
    }

    const formatted = splited
      .map((splitted) =>
        new Date(splitted).toLocaleDateString("en-US", options)
      )
      .join(" - ");

    return { ...item, [key]: formatted };
  }

  if (mapping === "quarter-to-fiscal") {
    return {
      ...item,
      [key]: setFiscalQuearterOffset(item[key], fiscalQuarterStartOffset),
    };
  }

  return item;
}

/* example how you can hide section in config
  "hiddenGroupings": [
    "Section 1",
    "Section 2"
  ],
  note: section will also make hidden columns under itself
*/
export const hideTableColumnsBySectionName = (
  headers,
  subTitles,
  hiddenGroupings,
  hiddenColumns = []
) => {
  const index = (headers ?? []).findIndex((header) =>
    (hiddenGroupings ?? []).includes(header)
  );

  return (subTitles ?? [])
    .filter((_, i) => i !== index)
    .map((subTitle) =>
      subTitle.filter((name) => !(hiddenColumns ?? []).includes(name))
    );
};

// foe cases where we have repeated column names under different groups
export function getJoinedName(header) {
  return header + "**";
}

/* example how you can hide columns in config 
  "hiddenColumns": [
    "Column1",
    "Column2"
  ],
*/
export const hideTableColumnsByColumnName = (
  subTitles,
  hiddenColumns,
  headers
) => {
  return subTitles.map((subTitle, index) =>
    subTitle.filter((sub) => {
      if (headers) {
        return !hiddenColumns.includes(getJoinedName(headers[index]) + sub);
      }

      return !hiddenColumns.includes(sub);
    })
  );
};

export const getTextAlignByColumnType = (
  column,
  isFormula,
  dynamicHeadersAlign = {},
  meta,
  textAlignOverride
) => {
  if ((textAlignOverride ?? {})[column]) {
    return textAlignOverride[column];
  }

  if (dynamicHeadersAlign[column]) {
    return dynamicHeadersAlign[column];
  }

  if (isFormula) {
    return "right";
  }

  const type = getMappingType(meta?.fields, column);
  return rightAligns.includes(type) ? "right" : "left";
};

export const setLimitValue = (value, rowsQuantity) => {
  if (value > rowsQuantity) {
    return rowsQuantity;
  }

  if (value < 0) {
    return 0;
  }

  return value;
};

export const getEnumColors = ({
  enumColorsSettings,
  cellKey,
  cellValueObject,
  row,
  groupingIndex,
}) => {
  const settings = (enumColorsSettings || []).find(
    (setting) => setting.column === cellKey
  );

  if (!settings) {
    return {};
  }

  const { asBackground, useForColumn, conditionalColumn } = settings;

  if (useForColumn) {
    return setForAllColumn(settings);
  }

  if (conditionalColumn) {
    return setConditionalColors(settings, row, groupingIndex);
  }

  // simple color config
  const value = settings[cellValueObject?.value];
  if (asBackground) {
    return setAsBackground(value);
  }

  return { color: value };
};

/**
 * Builds the base cell style object used by TableTd and GroupingColumn:
 * textAlign, enumColors, color, and textFormat overrides.
 */
export function buildTableCellStyles({
  enumColorsSettings,
  cellKey,
  cellValueObject,
  row,
  groupingIndex,
  textAlignOverride,
  textFormatOverride,
  fallbackColor,
}) {
  const enumColorStyles =
    getEnumColors({
      enumColorsSettings,
      cellKey,
      cellValueObject,
      row,
      groupingIndex,
    }) || {};

  const textAlign =
    (textAlignOverride ?? {})[cellKey] ?? (cellValueObject || {}).align;

  const { italic, bold, underline, strikethrough } =
    (textFormatOverride ?? {})[cellKey] ?? {};

  return {
    textAlign,
    ...enumColorStyles,
    color: enumColorStyles?.color ?? fallbackColor ?? undefined,
    ...(italic && { fontStyle: "italic" }),
    ...(bold && { fontWeight: "bold" }),
    ...(underline && { textDecoration: "underline" }),
    ...(strikethrough && { textDecoration: "line-through" }),
  };
}

function setAsBackground(value) {
  return {
    background: value,
    color: "white",
  };
}

function setForAllColumn(settings) {
  const { asTextColor, asBackground, color } = settings;

  return {
    ...(asTextColor && { color }),
    ...(asBackground && {
      background: color,
      color: asTextColor ? color : "white",
    }),
  };
}

function getColorByDymanicOperator(values, value) {
  return (values || []).find((v) => v.value === value)?.color;
}

function getBackgroundByDymanicOperator(values, value) {
  return (values || []).find((v) => v.value === value)?.background;
}

function setConditionalColors(settings, row, groupingIndex) {
  if (!row.values) {
    return null;
  }

  const clusterValuesIndex =
    groupingIndex && groupingIndex !== -1 ? groupingIndex - 1 : 0;

  const { conditionalColumn, values, drilldownConditionalIcon } = settings;
  const rowData = row.values[clusterValuesIndex || 0] ?? {};
  const value = rowData[conditionalColumn];

  return {
    color: getColorByDymanicOperator(values, value),
    background: getBackgroundByDymanicOperator(values, value),
    drilldownIcon: rowData[drilldownConditionalIcon],
  };
}

export function replaceToGroupDisplayKey(row, rowGroupKey, rowGroupDisplayKey) {
  if (!rowGroupDisplayKey) {
    return (
      row[rowGroupKey] ||
      (row.values && row.values[0] ? row.values[0][rowGroupKey] : null)
    );
  }

  // the regular data structure usually stays the same and we always have a grouped column that repeats and only the values change,
  // for such data we can use index 0 but for P&L table any data group can have an undefined anchor element that can be defined either at index 0 or at 10,
  // so here we take 1 existing
  const index = row.values.findIndex((value) => value[rowGroupDisplayKey]);
  return row.values[index < 0 ? 0 : index][rowGroupDisplayKey];
}

export function geRowGroupKeyLinkData(linkColumns, rowGroupKey, row) {
  const isMatch = (linkColumns ?? []).find((col) => col.key === rowGroupKey);

  if (isMatch) {
    return (
      (row ?? {})[isMatch.urlKey] ||
      ((row?.values ?? [{}]).find((item) => item[isMatch.urlKey]) ?? {})[
        isMatch.urlKey
      ]
    );
  }
}

export function buildApiStyles(style) {
  return (style + "")
    .split(";")
    .filter(Boolean)
    .map((rule) => `${rule.trim()} !important`)
    .join("; ");
}

export function applyApiStyles(row, excludeApiStylesForRowGroupKeys) {
  if (excludeApiStylesForRowGroupKeys) {
    return null;
  }

  const values = row?.values ?? [];
  const [firstValue, existingValue] = [
    values[0]?.style,
    values.find((v) => v.style)?.style,
  ];

  return buildApiStyles(firstValue ?? existingValue ?? "");
}
